/**
 * Cash register KV repository (Phase 3/4) — newCashRegisterData_* + merged snapshots.
 */

import newCashRegisterService from '../../services/newCashRegisterService';
import { queueBusinessPayloadWrite } from '../storage/businessPayloadWrite';

const MAX_SNAPSHOTS = 100;

/** Delegiert an newCashRegisterService (schrittweise Migration der Aufrufer). */
export class KvCashRegisterRepository {
  getCurrentBusinessId() {
    return newCashRegisterService.getCurrentBusinessId();
  }

  getRegisterData(businessId) {
    return newCashRegisterService.getBusinessData(businessId);
  }

  saveRegisterData(businessId, data) {
    return newCashRegisterService.saveBusinessData(data, businessId);
  }

  getActiveSession(businessId) {
    return newCashRegisterService.getActiveSession(businessId);
  }

  getPreviousSessionAmount(businessId) {
    return newCashRegisterService.getPreviousSessionAmount(businessId);
  }

  getSessionCashBalance(businessId) {
    return newCashRegisterService.getSessionCashBalance(businessId);
  }

  createSession(workerId, workerName, initialAmount, businessId) {
    return newCashRegisterService.createSession(workerId, workerName, initialAmount, businessId);
  }

  endSession(workerId, finalAmount, businessId) {
    return newCashRegisterService.endSession(workerId, finalAmount, businessId);
  }

  pauseSession(workerId, businessId) {
    return newCashRegisterService.pauseSession(workerId, businessId);
  }

  resumeSession(workerId, businessId) {
    return newCashRegisterService.resumeSession(workerId, businessId);
  }

  addTransaction(transaction, businessId) {
    return newCashRegisterService.addTransaction(transaction, businessId);
  }

  addDeviation(deviation, businessId) {
    return newCashRegisterService.addDeviation(deviation, businessId);
  }

  getSessionHistory(businessId, limit) {
    return newCashRegisterService.getSessionHistory(businessId, limit);
  }

  getBusinessStatistics(businessId) {
    return newCashRegisterService.getBusinessStatistics(businessId);
  }

  exportSessionData(sessionId, businessId) {
    return newCashRegisterService.exportSessionData(sessionId, businessId);
  }

  repairOrphanSessions(businessId) {
    return newCashRegisterService.repairOrphanSessions(businessId);
  }

  forceClearActiveSession(businessId) {
    return newCashRegisterService.forceClearActiveSession(businessId);
  }

  calculateSessionDeviation(sessionId, businessId) {
    return newCashRegisterService.calculateSessionDeviation(sessionId, businessId);
  }

  markSaleTransactionsVoided(businessId, saleId, options) {
    return newCashRegisterService.markSaleTransactionsVoided(businessId, saleId, options);
  }

  /**
   * Session-Snapshot atomar im merged business blob.
   */
  async appendSessionSnapshot(businessId, snapshot) {
    if (!businessId || !snapshot) {
      return { success: false, error: 'missing businessId or snapshot' };
    }

    await queueBusinessPayloadWrite(
      businessId,
      (parsed) => {
        const reports =
          parsed.reports && typeof parsed.reports === 'object'
            ? { ...parsed.reports }
            : {};
        if (!Array.isArray(reports.sessionSnapshots)) {
          reports.sessionSnapshots = [];
        }
        reports.sessionSnapshots.push(snapshot);
        if (reports.sessionSnapshots.length > MAX_SNAPSHOTS) {
          reports.sessionSnapshots = reports.sessionSnapshots.slice(-MAX_SNAPSHOTS);
        }
        parsed.reports = reports;
        parsed.sessionSnapshots = reports.sessionSnapshots;
        return parsed;
      },
      { allowInSafeMode: true, authoritativeFields: ['reports'] }
    );

    return { success: true, snapshot };
  }
}
